import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePerformanceChartQuery } from '../api/usePerformanceChartQuery';
import { usePeriod } from './usePeriod';
import { useRouteParams } from './useRouteParams';
import { selectGroupSelectionData } from '../state/selectors';
import {
  setGroupSelectionTabsList,
  setGroupSelectionDefaultTabIndex,
  setGroupSelectionLoadingStatus,
} from '../state/actions';
import { TabSlug } from '../types';
import { SERIES_COLORS } from '../../CommonComponents/OPL/charts/charts.constants';

export const usePerformanceChartCard = () => {
  const dispatch = useDispatch();

  const {
    tabList: stateTabList,
    defaultTabIndex: stateDefaultTabIndex,
    selectedTabIndex: stateSelectedTabIndex,
  } = useSelector(selectGroupSelectionData(TabSlug.Performance));

  const { accountId, clientId, groupId } = useRouteParams();

  const period = usePeriod();
  const startDate = period.dates[0];
  const endDate = period.dates[1];

  const queryInfo = usePerformanceChartQuery({
    clientId,
    period: period.value,
    startDate,
    endDate,
    accountId,
    groupId,
  });

  useEffect(() => {
    dispatch(setGroupSelectionLoadingStatus(queryInfo.isLoading, TabSlug.Performance));
  }, [dispatch, queryInfo.isLoading]);

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const { defaultGroup, groups } = queryInfo.data;

    const tabList = stateTabList ?? groups.map(({ name }) => name);
    const defaultGroupIndex =
      stateDefaultTabIndex ?? tabList.findIndex(name => name === defaultGroup);
    const defaultTabIndex = defaultGroupIndex === -1 ? 0 : defaultGroupIndex;
    const tabIndex = stateSelectedTabIndex ?? defaultTabIndex;

    const selectedGroup = groups[tabIndex];

    const categories = selectedGroup.series.map(({ name }) => name);

    const series = selectedGroup.series.map(({ color, name, value }, index) => ({
      color: color || SERIES_COLORS[index % SERIES_COLORS.length],
      name,
      y: value,
    }));

    if (categories.length > 0) {
      dispatch(setGroupSelectionTabsList(tabList, TabSlug.Performance));
    }

    if (!stateDefaultTabIndex) {
      dispatch(setGroupSelectionDefaultTabIndex(defaultGroupIndex, TabSlug.Performance));
    }

    return {
      series: [{ data: series }],
      categories,
    };
  }, [dispatch, queryInfo.data, stateDefaultTabIndex, stateSelectedTabIndex, stateTabList]);

  return {
    ...queryInfo,
    data,
  };
};
